(function () {
  if (window.ElegooTieredDiscountUtils) return;

  const parseInteger = (value, fallback = 0) => {
    const parsed = parseInt(String(value ?? ""), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseDecimal = (value, fallback = 0) => {
    const parsed = parseFloat(String(value ?? ""));
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseJson = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  const normalizeBasis = (value) =>
    String(value || "").toLowerCase() === "amount" ? "amount" : "quantity";

  const normalizeValueType = (value) =>
    String(value || "").toLowerCase() === "amount" ? "amount" : "percentage";

  const getTierThreshold = (tier, basis) =>
    basis === "amount"
      ? parseInteger(tier?.amountCents, 0)
      : parseInteger(tier?.qty, 0);

  const getProgressThreshold = (tier, basis) =>
    tier?.threshold != null
      ? parseInteger(tier.threshold, 0)
      : getTierThreshold(tier, basis);

  const getExtendedProgressMax = (tiers, basis) => {
    if (!Array.isArray(tiers) || !tiers.length) return 0;
    const last = getProgressThreshold(tiers[tiers.length - 1], basis);
    if (!last) return 0;

    const prev =
      tiers.length >= 2
        ? getProgressThreshold(tiers[tiers.length - 2], basis)
        : 0;
    const lastGap = prev > 0 ? Math.max(0, last - prev) : 0;
    const defaultExt = Math.round(last * 0.06);
    const gapExt = lastGap > 0 ? Math.round(lastGap * 0.5) : 0;
    const cap = Math.max(1, Math.round(last * 0.25));
    const extension = Math.min(Math.max(1, defaultExt, gapExt), cap);

    return last + extension;
  };

  const normalizeTiers = (rawTiers, basis, valueType) => {
    const input = Array.isArray(rawTiers) ? rawTiers : [];
    return input
      .map((tier) => {
        const normalized = {
          qty: parseInteger(tier?.qty, 0),
          amountCents: parseInteger(tier?.amountCents, 0),
          percent: parseDecimal(tier?.percent, 0),
          amountOffCents: parseInteger(tier?.amountOffCents, 0),
        };
        return { threshold: getTierThreshold(normalized, basis), tier: normalized };
      })
      .filter(
        ({ threshold, tier }) =>
          threshold > 0 &&
          (valueType === "amount" ? tier.amountOffCents > 0 : tier.percent > 0),
      )
      .sort((left, right) => left.threshold - right.threshold)
      .map(({ tier }) => tier);
  };

  const normalizeCampaigns = (rawCampaigns, options = {}) =>
    (Array.isArray(rawCampaigns) ? rawCampaigns : [])
      .map((campaign) => {
        const discountId = String(campaign?.discountId || "").trim();
        const basis = normalizeBasis(campaign?.basis);
        const valueType = normalizeValueType(campaign?.valueType);
        const tiers = normalizeTiers(campaign?.tiers, basis, valueType);

        if (!discountId || !tiers.length) return null;

        const normalized = {
          discountId,
          discountTitle: "",
          valueType,
          basis,
          tiers,
        };

        if (options.includeTitleMessage) {
          normalized.discountTitle = String(campaign?.discountTitle || "").trim();
        }

        return normalized;
      })
      .filter(Boolean);

  const parseDiscountTiers = (raw, basis) => {
    if (!raw) return null;
    const parsed = parseJson(raw, null);
    if (!Array.isArray(parsed)) return null;
    const normalizedBasis = basis == null ? null : normalizeBasis(basis);
    const mapped = parsed
      .map((tier) => ({
        qty: parseInteger(tier?.qty, 0),
        amountCents: parseInteger(tier?.amountCents, 0),
        percent: parseDecimal(tier?.percent, 0),
        amountOffCents: parseInteger(tier?.amountOffCents, 0),
      }))
      .filter(
        (tier) =>
          (tier.percent > 0 || tier.amountOffCents > 0) &&
          (tier.qty > 0 || tier.amountCents > 0),
      );
    if (!mapped.length) return null;
    if (normalizedBasis == null) return mapped;

    const tiers = mapped
      .map((tier) => ({
        threshold: getTierThreshold(tier, normalizedBasis),
        tier,
      }))
      .filter(({ threshold }) => threshold > 0)
      .sort((left, right) => left.threshold - right.threshold)
      .map(({ tier }) => tier);
    return tiers.length ? tiers : null;
  };

  const parseDiscountCampaigns = (raw, options = {}) => {
    if (!raw) return [];
    return normalizeCampaigns(parseJson(raw, []), options);
  };

  const findBestTier = (tiers, basis, metric) => {
    let matched = null;
    (Array.isArray(tiers) ? tiers : []).forEach((tier) => {
      const threshold = getTierThreshold(tier, basis);
      if (threshold > 0 && metric >= threshold) matched = tier;
    });
    return matched;
  };

  const getCurrentTier = (tiers, basis, metric) =>
    findBestTier(tiers, basis, parseInteger(metric, 0)) || {
      qty: 0,
      amountCents: 0,
      percent: 0,
      amountOffCents: 0,
    };

  const getNextTier = (tiers, basis, metric) => {
    const normalizedMetric = parseInteger(metric, 0);
    return (Array.isArray(tiers) ? tiers : []).find(
      (tier) => normalizedMetric < getTierThreshold(tier, basis),
    ) || null;
  };

  const campaignTierForLineIndexes = (campaign, lineIndexes, lineInfos) => {
    if (!campaign || !Array.isArray(lineIndexes) || !lineIndexes.length) return null;

    let totalQuantity = 0;
    let totalAmountCents = 0;
    lineIndexes.forEach((lineIndex) => {
      totalQuantity += lineInfos[lineIndex]?.quantity || 0;
      totalAmountCents += lineInfos[lineIndex]?.subtotalCents || 0;
    });
    const metricValue = campaign.basis === "amount" ? totalAmountCents : totalQuantity;

    return {
      metricValue,
      subtotalCents: totalAmountCents,
      tier: getCurrentTier(campaign.tiers, campaign.basis, metricValue),
    };
  };

  const campaignDiscountCentsForLineIndexes = (campaign, lineIndexes, lineInfos) => {
    const resolved = campaignTierForLineIndexes(campaign, lineIndexes, lineInfos);
    if (!resolved) return 0;
    const subtotalCents = parseInteger(resolved.subtotalCents, 0);
    if (subtotalCents <= 0) return 0;

    if (normalizeValueType(campaign.valueType) === "amount") {
      const amountOffCents = parseInteger(resolved.tier?.amountOffCents, 0);
      if (amountOffCents <= 0) return 0;
      return Math.min(amountOffCents, subtotalCents);
    }

    const percent = parseDecimal(resolved.tier?.percent, 0);
    if (percent <= 0) return 0;
    // 逐 unit floor 汇总，和 Shopify Functions (Rust 整数除法) 对齐。
    // subtotalCents 是整数，= unitPrice × quantity，所以 unitPrice = subtotal/quantity 精确。
    // 例: 19.99 × 3 × 10%: floor(1999*0.1)×3 = 199×3 = 597 ($5.97)
    //     如果对整组算 floor: floor(5997*0.1) = 599 ($5.99) — 与 Shopify 不一致
    return lineIndexes.reduce((sum, lineIndex) => {
      const lineSubtotal = parseInteger(lineInfos[lineIndex]?.subtotalCents, 0);
      const lineQty = parseInteger(lineInfos[lineIndex]?.quantity, 0);
      if (lineSubtotal <= 0 || lineQty <= 0) return sum;
      const unitDiscountFloor = Math.floor((lineSubtotal / lineQty) * percent / 100);
      return sum + unitDiscountFloor * lineQty;
    }, 0);
  };

  const eligibleLineIndexesForCampaign = (
    campaignIndex,
    remainingLineIndexes,
    lineInfos,
  ) =>
    remainingLineIndexes.filter((lineIndex) =>
      lineInfos[lineIndex]?.applicableCampaignIndexes?.includes(campaignIndex),
    );

  const finalizeAssignments = (groupedLineIndexes, campaigns, lineInfos) => {
    let totalDiscountCents = 0;
    const assignments = new Map();
    const campaignGroups = new Map();

    groupedLineIndexes.forEach((groupLineIndexes, campaignIndex) => {
      const campaign = campaigns[campaignIndex];
      const discountCents = campaignDiscountCentsForLineIndexes(
        campaign,
        groupLineIndexes,
        lineInfos,
      );
      if (discountCents <= 0) return;

      totalDiscountCents += discountCents;

      campaignGroups.set(campaignIndex, [...groupLineIndexes]);
      groupLineIndexes.forEach((lineIndex) => {
        assignments.set(lineIndex, { campaignIndex });
      });
    });

    return { totalDiscountCents, assignments, campaignGroups };
  };

  const greedyAssignmentsForComponent = (
    componentLineIndexes,
    campaigns,
    lineInfos,
  ) => {
    let remainingLineIndexes = [...componentLineIndexes];
    const finalAssignments = new Map();

    while (remainingLineIndexes.length) {
      const currentCampaignDiscounts = campaigns.map((campaign, campaignIndex) => {
        const eligibleLineIndexes = eligibleLineIndexesForCampaign(
          campaignIndex,
          remainingLineIndexes,
          lineInfos,
        );
        return campaignDiscountCentsForLineIndexes(campaign, eligibleLineIndexes, lineInfos);
      });

      const tentativeGroups = new Map();

      remainingLineIndexes.forEach((lineIndex) => {
        const line = lineInfos[lineIndex];
        let bestCampaignIndex = null;
        let bestDiscountCents = 0;

        (line?.applicableCampaignIndexes || []).forEach((campaignIndex) => {
          const currentDiscountCents = currentCampaignDiscounts[campaignIndex] || 0;
          if (currentDiscountCents > bestDiscountCents) {
            bestDiscountCents = currentDiscountCents;
            bestCampaignIndex = campaignIndex;
          }
        });

        if (bestCampaignIndex !== null && bestDiscountCents > 0) {
          const group = tentativeGroups.get(bestCampaignIndex) || [];
          group.push(lineIndex);
          tentativeGroups.set(bestCampaignIndex, group);
        }
      });

      if (!tentativeGroups.size) break;

      const finalizedLineIndexes = [];
      tentativeGroups.forEach((groupLineIndexes, campaignIndex) => {
        const discountCents = campaignDiscountCentsForLineIndexes(
          campaigns[campaignIndex],
          groupLineIndexes,
          lineInfos,
        );
        if (discountCents <= 0) return;

        groupLineIndexes.forEach((lineIndex) => {
          finalAssignments.set(lineIndex, { campaignIndex });
          finalizedLineIndexes.push(lineIndex);
        });
      });

      if (!finalizedLineIndexes.length) break;

      const finalizedSet = new Set(finalizedLineIndexes);
      remainingLineIndexes = remainingLineIndexes.filter(
        (lineIndex) => !finalizedSet.has(lineIndex),
      );
    }

    return finalAssignments;
  };

  const optimizeComponent = (
    componentLineIndexes,
    campaigns,
    lineInfos,
    maxExactSharedLines,
  ) => {
    const groupedLineIndexes = new Map();
    const sharedLineIndexes = [];

    componentLineIndexes.forEach((lineIndex) => {
      const applicableCampaignIndexes =
        lineInfos[lineIndex]?.applicableCampaignIndexes || [];

      if (applicableCampaignIndexes.length === 1) {
        const campaignIndex = applicableCampaignIndexes[0];
        const group = groupedLineIndexes.get(campaignIndex) || [];
        group.push(lineIndex);
        groupedLineIndexes.set(campaignIndex, group);
        return;
      }

      if (applicableCampaignIndexes.length > 1) {
        sharedLineIndexes.push(lineIndex);
      }
    });

    if (!sharedLineIndexes.length) {
      return finalizeAssignments(groupedLineIndexes, campaigns, lineInfos).assignments;
    }

    sharedLineIndexes.sort((leftIndex, rightIndex) => {
      const left = lineInfos[leftIndex];
      const right = lineInfos[rightIndex];
      return (
        (right?.applicableCampaignIndexes?.length || 0) -
          (left?.applicableCampaignIndexes?.length || 0) ||
        (right?.subtotalCents || 0) - (left?.subtotalCents || 0) ||
        (right?.quantity || 0) - (left?.quantity || 0)
      );
    });

    if (sharedLineIndexes.length > maxExactSharedLines) {
      return greedyAssignmentsForComponent(
        componentLineIndexes,
        campaigns,
        lineInfos,
      );
    }

    let bestTotalDiscountCents = -1;
    let bestAssignments = new Map();

    const dfs = (position) => {
      if (position >= sharedLineIndexes.length) {
        const result = finalizeAssignments(groupedLineIndexes, campaigns, lineInfos);
        if (result.totalDiscountCents > bestTotalDiscountCents) {
          bestTotalDiscountCents = result.totalDiscountCents;
          bestAssignments = result.assignments;
        }
        return;
      }

      const lineIndex = sharedLineIndexes[position];

      dfs(position + 1);

      (lineInfos[lineIndex]?.applicableCampaignIndexes || []).forEach(
        (campaignIndex) => {
          const group = groupedLineIndexes.get(campaignIndex) || [];
          group.push(lineIndex);
          groupedLineIndexes.set(campaignIndex, group);

          dfs(position + 1);

          const nextGroup = groupedLineIndexes.get(campaignIndex) || [];
          nextGroup.pop();
          if (nextGroup.length) {
            groupedLineIndexes.set(campaignIndex, nextGroup);
          } else {
            groupedLineIndexes.delete(campaignIndex);
          }
        },
      );
    };

    dfs(0);
    return bestAssignments;
  };

  const collectConnectedComponents = (campaignToLineIndexes, lineInfos) => {
    const visitedLines = new Array(lineInfos.length).fill(false);
    const visitedCampaigns = new Array(campaignToLineIndexes.length).fill(false);
    const components = [];

    for (let startLineIndex = 0; startLineIndex < lineInfos.length; startLineIndex += 1) {
      if (visitedLines[startLineIndex]) continue;

      const componentLineIndexes = [];
      const pendingLineIndexes = [startLineIndex];

      while (pendingLineIndexes.length) {
        const lineIndex = pendingLineIndexes.pop();
        if (visitedLines[lineIndex]) continue;

        visitedLines[lineIndex] = true;
        componentLineIndexes.push(lineIndex);

        (lineInfos[lineIndex]?.applicableCampaignIndexes || []).forEach(
          (campaignIndex) => {
            if (visitedCampaigns[campaignIndex]) return;
            visitedCampaigns[campaignIndex] = true;
            (campaignToLineIndexes[campaignIndex] || []).forEach(
              (candidateLineIndex) => {
                if (!visitedLines[candidateLineIndex]) {
                  pendingLineIndexes.push(candidateLineIndex);
                }
              },
            );
          },
        );
      }

      if (componentLineIndexes.length) components.push(componentLineIndexes);
    }

    return components;
  };

  const optimizeDiscountAssignments = (
    campaigns,
    rawLines,
    options = {},
  ) => {
    const normalizedCampaigns = Array.isArray(campaigns) ? campaigns : [];
    const normalizedRawLines = Array.isArray(rawLines) ? rawLines : [];
    const maxExactSharedLines = parseInteger(options.maxExactSharedLines, 12);
    const mode = String(options.mode || "").trim();
    const requiredLineId = String(options.requiredLineId || "").trim();
    const emptyResult = {
      campaigns: normalizedCampaigns,
      lineInfos: [],
      assignments: new Map(),
      campaignGroups: new Map(),
      totalDiscountCents: 0,
    };

    if (!normalizedCampaigns.length || !normalizedRawLines.length) {
      return emptyResult;
    }

    const campaignIndexById = new Map(
      normalizedCampaigns.map((campaign, index) => [campaign.discountId, index]),
    );

    const lineInfos = normalizedRawLines
      .map((line) => ({
        id: String(line?.id || "").trim(),
        quantity: parseInteger(line?.quantity, 0),
        subtotalCents: parseInteger(line?.subtotalCents, 0),
        applicableCampaignIndexes: (Array.isArray(line?.campaignIds)
          ? line.campaignIds
          : []
        )
          .map((discountId) => campaignIndexById.get(String(discountId || "").trim()))
          .filter((campaignIndex) => Number.isInteger(campaignIndex)),
      }))
      .filter(
        (line) =>
          line.id &&
          line.quantity > 0 &&
          line.subtotalCents >= 0 &&
          line.applicableCampaignIndexes.length,
      );

    if (!lineInfos.length) {
      return {
        ...emptyResult,
        lineInfos,
      };
    }

    if (mode === "single_best") {
      const requiredLineIndex = requiredLineId
        ? lineInfos.findIndex((line) => line.id === requiredLineId)
        : -1;
      const requiredCampaignIndexes =
        requiredLineIndex >= 0
          ? new Set(lineInfos[requiredLineIndex]?.applicableCampaignIndexes || [])
          : null;

      const candidates = normalizedCampaigns
        .map((campaign, campaignIndex) => {
          if (requiredCampaignIndexes && !requiredCampaignIndexes.has(campaignIndex)) {
            return null;
          }

          const groupLineIndexes = [];
          for (let lineIndex = 0; lineIndex < lineInfos.length; lineIndex += 1) {
            if (lineInfos[lineIndex].applicableCampaignIndexes.includes(campaignIndex)) {
              groupLineIndexes.push(lineIndex);
            }
          }

          return buildCampaignResult(campaign, groupLineIndexes, lineInfos, {
            campaignIndex,
          });
        })
        .filter(Boolean);

      const best = selectBestCampaignResult(candidates) || candidates[0] || null;
      if (!best) {
        return {
          ...emptyResult,
          lineInfos,
        };
      }

      const assignments = new Map();
      const campaignGroups = new Map();
      const bestLineIndexes = Array.isArray(best.groupLineIndexes) ? best.groupLineIndexes : [];

      if (bestLineIndexes.length) {
        campaignGroups.set(best.campaignIndex, [...bestLineIndexes]);
        bestLineIndexes.forEach((lineIndex) => {
          assignments.set(lineIndex, { campaignIndex: best.campaignIndex });
        });
      }

      return {
        campaigns: normalizedCampaigns,
        lineInfos,
        assignments,
        campaignGroups,
        totalDiscountCents: parseInteger(best.discountCents, 0),
      };
    }

    const finalAssignments = new Map();
    const campaignToLineIndexes = normalizedCampaigns.map(() => []);
    lineInfos.forEach((line, lineIndex) => {
      (Array.isArray(line.applicableCampaignIndexes)
        ? line.applicableCampaignIndexes
        : []
      ).forEach((campaignIndex) => {
        const bucket = campaignToLineIndexes[campaignIndex];
        if (bucket) bucket.push(lineIndex);
      });
    });

    collectConnectedComponents(campaignToLineIndexes, lineInfos).forEach(
      (componentLineIndexes) => {
        const componentAssignments = optimizeComponent(
          componentLineIndexes,
          normalizedCampaigns,
          lineInfos,
          maxExactSharedLines,
        );
        componentAssignments.forEach((assignment, lineIndex) => {
          finalAssignments.set(lineIndex, assignment);
        });
      },
    );

    const campaignGroups = new Map();
    let totalDiscountCents = 0;

    finalAssignments.forEach((assignment, lineIndex) => {
      const group = campaignGroups.get(assignment.campaignIndex) || [];
      group.push(lineIndex);
      campaignGroups.set(assignment.campaignIndex, group);
    });

    campaignGroups.forEach((lineIndexes, campaignIndex) => {
      totalDiscountCents += campaignDiscountCentsForLineIndexes(
        normalizedCampaigns[campaignIndex],
        lineIndexes,
        lineInfos,
      );
    });

    return {
      campaigns: normalizedCampaigns,
      lineInfos,
      assignments: finalAssignments,
      campaignGroups,
      totalDiscountCents,
    };
  };

  const buildCampaignResult = (
    campaign,
    groupLineIndexes,
    lineInfos,
    options = {},
  ) => {
    const normalizedLineIndexes = Array.isArray(groupLineIndexes)
      ? groupLineIndexes
      : [];
    if (!campaign || !normalizedLineIndexes.length) return null;

    const metricValue = normalizedLineIndexes.reduce((sum, lineIndex) => {
      const line = lineInfos[lineIndex];
      return sum + (campaign.basis === "amount" ? line?.subtotalCents || 0 : line?.quantity || 0);
    }, 0);
    const subtotalCents = normalizedLineIndexes.reduce(
      (sum, lineIndex) => sum + (lineInfos[lineIndex]?.subtotalCents || 0),
      0,
    );
    const valueType = normalizeValueType(campaign.valueType);
    const tier = getCurrentTier(campaign.tiers, campaign.basis, metricValue);
    const percent = parseDecimal(tier?.percent, 0);
    const amountOffCents = parseInteger(tier?.amountOffCents, 0);
    const nextTier = getNextTier(campaign.tiers, campaign.basis, metricValue);
    // 百分比折扣必须逐 unit floor 后汇总，和 Shopify Functions (Rust 整数除法) 对齐。
    // 先对每条 line 算出 unitPrice = subtotalCents / quantity（整数域精确），
    // 再对 unit 级折扣 floor，最后 × quantity 汇总。
    // 例: 19.99 × 3 × 10% = 5.997
    //   整组 round:  Math.round(599.7) = 600 → $6.00 (gradient 旧实现)
    //   整组 floor:  Math.floor(599.7) = 599 → $5.99 (shopify 也不对)
    //   逐 unit floor: floor(199.9) × 3 = 199×3 = 597 → $5.97 (Shopify cart) ✓
    const computedDiscountCents =
      valueType === "amount"
        ? Math.min(amountOffCents, subtotalCents)
        : normalizedLineIndexes.reduce((sum, lineIndex) => {
            const lineSubtotal = lineInfos[lineIndex]?.subtotalCents || 0;
            const lineQty = lineInfos[lineIndex]?.quantity || 0;
            if (lineSubtotal <= 0 || lineQty <= 0) return sum;
            const unitDiscountFloor = Math.floor((lineSubtotal / lineQty) * percent / 100);
            return sum + unitDiscountFloor * lineQty;
          }, 0);

    return {
      campaign,
      campaignIndex: Number.isInteger(options.campaignIndex)
        ? options.campaignIndex
        : null,
      groupLineIndexes: [...normalizedLineIndexes],
      valueType,
      metricValue,
      tier,
      nextTier,
      percent,
      amountOffCents,
      subtotalCents,
      discountCents:
        options.discountCentsOverride == null
          ? computedDiscountCents
          : parseInteger(options.discountCentsOverride, 0),
      remainingToNext:
        options.remainingToNext == null
          ? nextTier == null
            ? Number.MAX_SAFE_INTEGER
            : Math.max(getTierThreshold(nextTier, campaign.basis) - metricValue, 0)
          : parseInteger(options.remainingToNext, 0),
      savingsAmount:
        options.savingsAmount == null
          ? options.discountCentsOverride == null
            ? computedDiscountCents
            : parseInteger(options.discountCentsOverride, 0)
          : parseInteger(options.savingsAmount, 0),
    };
  };

  const buildCampaignResultsFromSolution = (solution) => {
    const normalizedSolution = solution || {};
    const campaigns = Array.isArray(normalizedSolution.campaigns)
      ? normalizedSolution.campaigns
      : [];
    const lineInfos = Array.isArray(normalizedSolution.lineInfos)
      ? normalizedSolution.lineInfos
      : [];
    const campaignGroups =
      normalizedSolution.campaignGroups instanceof Map
        ? normalizedSolution.campaignGroups
        : new Map();
    const campaignResults = [];
    let totalDiscountCents = 0;

    campaignGroups.forEach((groupLineIndexes, campaignIndex) => {
      const result = buildCampaignResult(
        campaigns[campaignIndex],
        groupLineIndexes,
        lineInfos,
        { campaignIndex },
      );
      if (!result) return;
      totalDiscountCents += result.discountCents;
      campaignResults.push(result);
    });

    return {
      campaignResults,
      totalDiscountCents,
    };
  };

  const selectBestCampaignResult = (candidates, rules) => {
    const normalizedCandidates = (Array.isArray(candidates) ? candidates : []).filter(
      Boolean,
    );
    if (!normalizedCandidates.length) return null;

    const normalizedRules =
      Array.isArray(rules) && rules.length
        ? rules
        : [
            { field: "discountCents", order: "desc" },
            { field: "percent", order: "desc" },
            { field: "subtotalCents", order: "desc" },
          ];

    return normalizedCandidates.reduce((best, candidate) => {
      if (!best) return candidate;

      for (const rule of normalizedRules) {
        const left = candidate?.[rule.field];
        const right = best?.[rule.field];

        if (left === right) continue;
        if (rule.order === "asc") {
          return left < right ? candidate : best;
        }
        return left > right ? candidate : best;
      }

      return best;
    }, null);
  };

  const resolveProductDisplayCampaign = (solution, options = {}) => {
    const normalizedSolution = solution || {};
    const campaigns = Array.isArray(normalizedSolution.campaigns)
      ? normalizedSolution.campaigns
      : [];
    const lineInfos = Array.isArray(normalizedSolution.lineInfos)
      ? normalizedSolution.lineInfos
      : [];
    const assignments =
      normalizedSolution.assignments instanceof Map
        ? normalizedSolution.assignments
        : new Map();
    const campaignGroups =
      normalizedSolution.campaignGroups instanceof Map
        ? normalizedSolution.campaignGroups
        : new Map();
    const displayCampaignId = String(options.displayCampaignId || "").trim();
    const localLineId = String(options.localLineId || "").trim();
    const localQty = parseInteger(options.localQty, 0);
    const basePrice = parseInteger(options.basePrice, 0);

    if (!campaigns.length || !lineInfos.length) return null;

    const campaignIndexById = new Map(
      campaigns.map((campaign, index) => [campaign.discountId, index]),
    );

    if (displayCampaignId) {
      const campaignIndex = campaignIndexById.get(displayCampaignId);
      if (!Number.isInteger(campaignIndex)) return null;

      const result = buildCampaignResult(
        campaigns[campaignIndex],
        campaignGroups.get(campaignIndex) || [],
        lineInfos,
        {
          campaignIndex,
          savingsAmount: 0,
        },
      );
      if (!result) return null;

      return {
        campaign: result.campaign,
        groupLineIndexes: result.groupLineIndexes,
        metricValue: result.metricValue,
        currentTier: result.tier,
        nextTier: result.nextTier,
        savingsAmount: 0,
      };
    }

    if (!localLineId) return null;

    const localLineIndex = lineInfos.findIndex((line) => line.id === localLineId);
    if (localLineIndex < 0) return null;

    const localAssignment = assignments.get(localLineIndex);
    if (localAssignment) {
      const result = buildCampaignResult(
        campaigns[localAssignment.campaignIndex],
        campaignGroups.get(localAssignment.campaignIndex) || [],
        lineInfos,
        {
          campaignIndex: localAssignment.campaignIndex,
          savingsAmount: Math.round(
            (basePrice * localQty * parseDecimal(localAssignment.percent, 0)) / 100,
          ),
        },
      );
      if (!result) return null;

      return {
        campaign: result.campaign,
        groupLineIndexes: result.groupLineIndexes,
        metricValue: result.metricValue,
        currentTier: result.tier,
        nextTier: result.nextTier,
        savingsAmount: result.savingsAmount,
      };
    }

    const localLine = lineInfos[localLineIndex];
    const fallbackCandidates = (localLine?.applicableCampaignIndexes || [])
      .map((campaignIndex) => {
        const campaign = campaigns[campaignIndex];
        const assignedLineIndexes = campaignGroups.get(campaignIndex) || [];
        const groupLineIndexes = assignedLineIndexes.includes(localLineIndex)
          ? [...assignedLineIndexes]
          : [...assignedLineIndexes, localLineIndex];
        const baseResult = buildCampaignResult(
          campaign,
          groupLineIndexes,
          lineInfos,
          { campaignIndex },
        );
        if (!baseResult) return null;

        const localDiscountCents = Math.round(
          (basePrice * localQty * parseDecimal(baseResult.tier?.percent, 0)) / 100,
        );
        const nextThreshold = baseResult.nextTier
          ? getTierThreshold(baseResult.nextTier, campaign.basis)
          : Number.MAX_SAFE_INTEGER;

        return buildCampaignResult(campaign, groupLineIndexes, lineInfos, {
          campaignIndex,
          discountCentsOverride: localDiscountCents,
          savingsAmount: localDiscountCents,
          remainingToNext: Math.max(nextThreshold - baseResult.metricValue, 0),
        });
      })
      .filter(Boolean);

    const fallback = selectBestCampaignResult(fallbackCandidates, [
      { field: "discountCents", order: "desc" },
      { field: "percent", order: "desc" },
      { field: "remainingToNext", order: "asc" },
    ]);
    if (!fallback) return null;

    return {
      campaign: fallback.campaign,
      groupLineIndexes: fallback.groupLineIndexes,
      metricValue: fallback.metricValue,
      currentTier: fallback.tier,
      nextTier: fallback.nextTier,
      savingsAmount: fallback.savingsAmount,
    };
  };

  window.ElegooTieredDiscountUtils = {
    buildCampaignResult,
    buildCampaignResultsFromSolution,
    findBestTier,
    getCurrentTier,
    getExtendedProgressMax,
    getNextTier,
    getTierThreshold,
    normalizeBasis,
    normalizeCampaigns,
    normalizeTiers,
    optimizeDiscountAssignments,
    parseDecimal,
    parseDiscountCampaigns,
    parseDiscountTiers,
    parseInteger,
    parseJson,
    resolveProductDisplayCampaign,
    selectBestCampaignResult,
  };
})();
